import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Store, Truck, ShoppingCart } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { fcfa, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Loading } from "@/components/RequireAuth";
import { DemoBadge, PremiumBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";

const VariantPicker = ({ variants, value, onChange }) => (
  <div className="space-y-3">
    {variants.map((v) => (
      <div key={v.name}>
        <p className="mb-1.5 text-sm font-semibold">{v.name}</p>
        <div className="flex flex-wrap gap-2">
          {v.options.map((o) => (
            <button key={o} data-testid={`variant-${v.name}-${o}`} onClick={() => onChange({ ...value, [v.name]: o })}
              className={`rounded-lg px-3 py-1.5 text-sm ring-1 ${value[v.name] === o ? "bg-gray-900 text-white ring-gray-900" : "bg-white ring-gray-200 hover:ring-orange-400"}`}>{o}</button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function ProductPage() {
  const { id } = useParams();
  const { data: p, error } = useApi(`/products/${id}`);
  const { add, items } = useCart();
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState({});
  const [imgIdx, setImgIdx] = useState(0);
  if (error) return <p data-testid="product-error" className="p-10 text-center text-gray-500">{error}</p>;
  if (!p) return <Loading />;
  const variants = p.variants || [];
  const addToCart = () => {
    if (variants.some((v) => !sel[v.name])) return toast.error("Choisissez les options du produit");
    if (p.stock < 1) return toast.error("Produit en rupture de stock");
    if (items.length && (items[0].currency || "XOF") !== p.currency) return toast.error("Votre panier contient des produits dans une autre monnaie. Finalisez ou videz-le d'abord.");
    const variant = variants.map((v) => `${v.name}: ${sel[v.name]}`).join(", ") || null;
    add({ product_id: p.id, title: p.title, price: p.price, currency: p.currency, image: p.images?.[0], shop_id: p.shop.id, shop_name: p.shop.name, quantity: qty, variant, stock: p.stock });
    toast.success("Ajouté au panier");
  };

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-4 pb-28 pt-6 md:px-6 md:pb-10">
      {p.preview_only && <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200">Aperçu privé : ce produit n'est pas visible publiquement.</p>}
      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-3">
          <div className="aspect-square overflow-hidden rounded-2xl bg-orange-50">
            {p.images?.[imgIdx] && <img src={imgUrl(p.images[imgIdx])} alt={p.title} className="h-full w-full object-cover" data-testid="product-main-image" />}
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-2">{p.images.map((u, i) => <img key={u} src={imgUrl(u)} alt="" onClick={() => setImgIdx(i)} className={`h-16 w-16 cursor-pointer rounded-lg object-cover ring-2 ${i === imgIdx ? "ring-orange-500" : "ring-transparent"}`} />)}</div>
          )}
        </div>
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">{p.demo && <DemoBadge />}{p.shop?.premium && <PremiumBadge />}</div>
          <h1 data-testid="product-title" className="font-display text-3xl font-black leading-tight tracking-tight md:text-4xl">{p.title}</h1>
          <div className="flex items-baseline gap-3">
            <span data-testid="product-price" className="tnum font-display text-3xl font-extrabold text-orange-600">{fcfa(p.price, p.currency)}</span>
            {p.old_price && <span className="tnum text-gray-400 line-through">{fcfa(p.old_price, p.currency)}</span>}
          </div>
          <p data-testid="product-stock" className={`text-sm font-semibold ${p.stock > 0 ? "text-green-800" : "text-red-600"}`}>{p.stock > 0 ? `En stock (${p.stock})` : "Rupture de stock"}</p>
          {variants.length > 0 && <VariantPicker variants={variants} value={sel} onChange={setSel} />}
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full ring-1 ring-gray-200">
              <button data-testid="qty-minus" className="p-2.5" onClick={() => setQty(Math.max(1, qty - 1))}><Minus className="h-4 w-4" /></button>
              <span data-testid="qty-value" className="tnum w-8 text-center font-bold">{qty}</span>
              <button data-testid="qty-plus" className="p-2.5" onClick={() => setQty(Math.min(p.stock, qty + 1))}><Plus className="h-4 w-4" /></button>
            </div>
            <Button size="lg" className="hidden flex-1 rounded-full md:flex" onClick={addToCart} data-testid="add-to-cart-btn"><ShoppingCart className="mr-2 h-4 w-4" />Ajouter au panier</Button>
          </div>
          <Link to={`/boutique/${p.shop.slug}`} data-testid="product-shop-link" className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:border-orange-300">
            <img src={imgUrl(p.shop.logo)} alt="" className="h-11 w-11 rounded-lg object-cover" />
            <div><p className="text-xs text-gray-500">Vendu par</p><p className="flex items-center gap-1 font-bold"><Store className="h-4 w-4" />{p.shop.name}</p></div>
          </Link>
          <div><h2 className="mb-2 font-display text-lg font-bold">Description</h2><p data-testid="product-description" className="whitespace-pre-line text-gray-700">{p.description}</p>{p.sales_pitch && <p className="mt-3 text-gray-700">{p.sales_pitch}</p>}</div>
          {p.features?.length > 0 && (
            <div><h2 className="mb-2 font-display text-lg font-bold">Caractéristiques</h2>
              <dl className="divide-y rounded-xl border bg-white">{p.features.map((f, i) => <div key={i} className="flex justify-between gap-4 px-4 py-2 text-sm"><dt className="text-gray-500">{f.label}</dt><dd className="text-right font-medium">{f.value}</dd></div>)}</dl>
            </div>
          )}
          {p.shop.delivery?.zones?.length > 0 && (
            <div className="rounded-xl bg-green-50 p-4 text-sm">
              <p className="mb-2 flex items-center gap-2 font-bold text-green-900"><Truck className="h-4 w-4" />Livraison</p>
              {p.shop.delivery.zones.map((z) => <p key={z.name} className="flex justify-between text-green-900"><span>{z.name} · {z.delay}</span><span className="tnum font-semibold">{fcfa(z.fee, p.shop.currency)}</span></p>)}
              {p.shop.delivery.conditions && <p className="mt-2 text-green-800">{p.shop.delivery.conditions}</p>}
            </div>
          )}
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-14 z-30 border-t bg-white/90 p-3 backdrop-blur-xl md:hidden">
        <Button size="lg" className="w-full rounded-full" onClick={addToCart} data-testid="add-to-cart-mobile"><ShoppingCart className="mr-2 h-4 w-4" />Ajouter au panier · {fcfa(p.price * qty, p.currency)}</Button>
      </div>
    </div>
  );
}
